import { onBeforeUnmount, ref, watch, type Ref } from 'vue'

import {
  alignPopover,
  placePopover,
  type PopoverAlign,
  type PopoverSide,
} from '@/utils/popover-placement.util.js'

/**
 * Keeps an absolutely-positioned panel inside the viewport, the way a native select does.
 *
 * The decision itself is `placePopover` in `utils/` and is tested there; this is the DOM
 * half — measure, re-measure, clean up. Paired that way because the interesting failures
 * are arithmetic and a component test cannot produce a 150 px viewport with a
 * half-scrolled trigger, while this half is four lines that are either wired or not.
 *
 * ## Driven by the panel ref, not an `open` flag
 *
 * All three callers render their panel under `v-if`, so the element going from null to
 * non-null *is* the open event — and unlike a boolean it cannot be observed before the
 * element exists, which is the state a measurement needs. It also means no caller has to
 * keep a second flag in step with the one `usePopover` already owns.
 *
 * ## The natural height is measured ONCE
 *
 * `scrollHeight` is read on the frame the panel appears, while `maxHeight` is still null
 * and nothing is capping it. Re-reading it later would latch: by then the cap is applied
 * and the inner list is `flex-1 min-h-0`, so the content reports the clamped height, every
 * re-placement would shrink the panel a little more, and a few scroll events would grind
 * it to the floor.
 *
 * Holding the first measurement also fixes the side while a search filters the list.
 * A panel that re-chose its side on every keystroke would jump across its own trigger
 * mid-word, and `maxHeight` is only ever a cap — a list of two options still renders as
 * two options.
 */
export function usePopoverPlacement(
  /** The positioning context the panel is absolute within — a getter, so a caller may
      hand over a template ref, a prop, or anything else without a wrapper. */
  anchor: () => HTMLElement | null,
  panel: Ref<HTMLElement | null>,
): {
  side: Ref<PopoverSide>
  maxHeight: Ref<number | null>
  align: Ref<PopoverAlign>
  maxWidth: Ref<number | null>
} {
  const side = ref<PopoverSide>('below')
  const maxHeight = ref<number | null>(null)
  const align = ref<PopoverAlign>('start')
  const maxWidth = ref<number | null>(null)
  let natural: number | null = null
  let naturalWidth: number | null = null

  function place(): void {
    const box = anchor()?.getBoundingClientRect()

    if (box === undefined || natural === null || naturalWidth === null) return

    const placement = placePopover(
      { triggerTop: box.top, triggerBottom: box.bottom, viewportHeight: window.innerHeight },
      natural,
    )

    side.value = placement.side
    maxHeight.value = placement.maxHeight

    const alignment = alignPopover(
      { triggerLeft: box.left, triggerRight: box.right, viewportWidth: window.innerWidth },
      naturalWidth,
    )

    align.value = alignment.align
    maxWidth.value = alignment.maxWidth
  }

  // `capture: true` because the trigger may sit inside a scrolling pane — a table body,
  // a drawer — and a scroll event there does not bubble to the window. Capture sees every
  // scroll in the document on its way down regardless of which element produced it. The
  // flags must match between add and remove or the listener is never removed.
  const SCROLL = { capture: true, passive: true } as const

  function listen(): void {
    document.addEventListener('scroll', place, SCROLL)
    window.addEventListener('resize', place)
  }

  function unlisten(): void {
    document.removeEventListener('scroll', place, SCROLL)
    window.removeEventListener('resize', place)
  }

  watch(panel, (element) => {
    if (element === null) {
      unlisten()
      natural = null
      naturalWidth = null
      maxHeight.value = null
      maxWidth.value = null
      side.value = 'below'
      align.value = 'start'

      return
    }

    // `offsetHeight`, not `scrollHeight`: the comparison is against viewport space, which
    // is a border-box measurement, and so is `max-height` under Tailwind's preflight.
    // Mixing the two is what put a 2px scrollbar on every capped panel.
    const measured = element.offsetHeight

    // An unmeasurable panel stays UNCAPPED and opens downward — the behaviour before any
    // of this existed. `scrollHeight` is 0 for an element that has not been laid out: in
    // jsdom always, and in a browser whenever an ancestor is `display: none`. Zero is the
    // dangerous value rather than a harmless one, because a panel that wants no height
    // fits on either side, so `placePopover` would answer `below` with `maxHeight: 0` and
    // the style binding would collapse the list to an invisible strip.
    if (measured <= 0) return

    natural = measured
    // `scrollWidth`, not `offsetWidth`: the panel is `w-max`, so its box is already its
    // content width — but a panel the browser has had to constrain reports the narrower
    // box here and the content width there, and the content width is what is being asked
    // for. Zero is handled by the guard above.
    naturalWidth = Math.max(element.scrollWidth, element.offsetWidth)
    place()
    listen()
  },
  // `post`, because this callback MEASURES. A `pre` watcher is allowed to run before the
  // DOM is patched, and `scrollHeight` on an element that is not laid out yet is 0 — which
  // fits anywhere, so every panel would open downward and the whole flip would be dead
  // code that looked wired.
  { flush: 'post' })

  // A panel unmounted while open — a drawer closing under it — would otherwise leave a
  // scroll listener measuring an element that is gone. The same leak the drawer's own
  // teardown test exists to catch.
  onBeforeUnmount(unlisten)

  return { side, maxHeight, align, maxWidth }
}
